"use client"

import { Text, Button } from "@medusajs/ui"
import { ListBullet, Adjustments } from "@medusajs/icons"
import { formatCount } from "@/lib/util/format-count"
import type { SortOption, ViewMode } from "../../templates/search-template"

type SearchToolbarProps = {
  totalResults: number
  currentPage: number
  itemsPerPage: number
  sortBy: SortOption
  viewMode: ViewMode
  onSortChange: (sort: SortOption) => void
  onViewModeChange: (view: ViewMode) => void
  onItemsPerPageChange: (limit: number) => void
  loading: boolean
  onFiltersOpen?: () => void
  /** Shown instead of the counter when there are no results. */
  emptyText?: string
}

export default function SearchToolbar({
  totalResults,
  currentPage,
  itemsPerPage,
  sortBy,
  viewMode,
  onSortChange,
  onViewModeChange,
  onItemsPerPageChange,
  loading,
  onFiltersOpen,
  emptyText = "Ni najdenih izdelkov",
}: SearchToolbarProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalResults)

  const sortOptions = [
    { value: "relevance", label: "Ustreznost" },
    { value: "price_asc", label: "Cena: od najnižje do najvišje" },
    { value: "price_desc", label: "Cena: od najvišje do najnižje" },
  ]

  const itemsPerPageOptions = [12, 24, 48, 96]

  return (
    <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Results Counter */}
        <div className="flex items-center gap-4">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              <Text className="text-neutral-600">Iskanje...</Text>
            </div>
          ) : (
            <Text className="text-neutral-600">
              {totalResults > 0 ? (
                `Prikazujem ${startItem}-${endItem} od ${formatCount(totalResults)} izdelkov`
              ) : (
                emptyText
              )}
            </Text>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          {/* View Mode Toggle */}
          <div className="hidden lg:flex items-center border border-neutral-300 rounded-lg overflow-hidden">
            <button
              onClick={() => onViewModeChange("grid")}
              className={`p-2 transition-colors ${
                viewMode === "grid"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
              disabled={loading}
              title="Grid view"
            >
              ⊞
            </button>
            <button
              onClick={() => onViewModeChange("list")}
              className={`p-2 transition-colors ${
                viewMode === "list"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
              disabled={loading}
              title="List view"
            >
              <ListBullet className="w-4 h-4" />
            </button>
          </div>

          {/* Sort Dropdown */}
          <div className="hidden lg:flex items-center gap-2">
            <Text className="text-sm text-neutral-600 whitespace-nowrap">Razvrsti po:</Text>
            <select
              aria-label="Razvrsti po"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none bg-white"
              disabled={loading}
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mobile View - Stacked Layout */}
      <div className="lg:hidden mt-4 pt-4 border-t border-neutral-200">
        <div className="grid grid-cols-1 gap-3">
          {/* Sort */}
          <div>
            <Text className="text-xs text-neutral-600 mb-1">Razvrsti po</Text>
            <select
              aria-label="Razvrsti po"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none bg-white"
              disabled={loading}
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {onFiltersOpen && (
          <div className="mt-4">
            <Button
              variant="secondary"
              onClick={onFiltersOpen}
              className="w-full flex items-center justify-center gap-2"
            >
              <Adjustments className="w-4 h-4" />
              <span>Filtriraj & Razvrsti</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
} 